import { useEffect, useMemo, useState } from 'react';
import { useFormikContext } from 'formik';
import { Space } from 'antd';
import moment from 'moment';
import useCountries from 'hooks/useCountries';
import useCodelists from 'hooks/useCodelists';
import CountrySelect from 'components/new-ui/composed/selects/CountrySelect';
import { useMultiStepFormContext } from 'components/new-ui/composed/multistep-form/MultiStepFormProvider';
import FormDatePicker from 'components/new-ui/composed/formDatePicker/FormDatePicker';
import FormInput from 'components/new-ui/composed/formInput/FormInput';
import ReadonlyFormItem from 'components/new-ui/base/form/ReadonlyFormItem';
import TruckIconOutlined from 'components/icons/TruckIconOutlined';
import FormSectionHeader from 'components/new-ui/base/form/FormSectionHeader';
import { DeclarationTypes } from 'store/declarations/enums/common/declaration-types';
import { ContainerStatuses, ManifestFormFields, ManifestFormSteps, UK_COUNTRY_CODE } from '../../types';
import ContainerStatusField from '../ContainerStatusField';
import { getTransportTypeDisplay, TransportTypeOptions, TransportTypes } from 'utils/transportTypes';
import { getContainerStatusLabel } from 'utils/containerStatus';
import FormSelect from 'components/new-ui/composed/formSelect/FormSelect';
import UnLocodeSelect from 'components/new-ui/composed/selects/UnLocodeSelect';

const FORM_FIELDS_MAX_WIDTH = 375;
const DATE_TIME_FORMAT = 'MMMM D; HH:mm A';

export const TransportDetailsFields = {
    path: {
        containerIndication: 'transportDetails.containerIndication',
        transportTypeId: 'transportDetails.transportTypeId',
        carrierOrHaulier: 'transportDetails.carrierOrHaulier',
        countryOfDispatch: 'transportDetails.countryOfDispatch',
        departurePort: 'transportDetails.departurePort',
        customsOfficeOfPresentation: 'transportDetails.customsOfficeOfPresentation',
        meansIdentificationType: 'transportDetails.meansIdentificationType',
        meansId: 'transportDetails.meansId',
        nationalityTransportMeans: 'transportDetails.nationalityTransportMeans',
        vehicleRegistration: 'transportDetails.vehicleRegistration',
        departureDateTime: 'transportDetails.departureDateTime',
        arrivalDateTime: 'transportDetails.arrivalDateTime',
    },
    resetKey: {
        containerIndication: 'containerIndication',
        transportTypeId: 'transportTypeId',
        carrierOrHaulier: 'carrierOrHaulier',
        countryOfDispatch: 'countryOfDispatch',
        departurePort: 'transportDetails.departurePort',
        customsOfficeOfPresentation: 'customsOfficeOfPresentation',
        meansIdentificationType: 'meansIdentificationType',
        meansId: 'meansId',
        nationalityTransportMeans: 'nationalityTransportMeans',
        vehicleRegistration: 'vehicleRegistration',
        departureDateTime: 'departureDateTime',
        arrivalDateTime: 'arrivalDateTime',
    },
} as const;

type TransportDetailsResetKey = typeof TransportDetailsFields.resetKey[keyof typeof TransportDetailsFields.resetKey];

const TransportDetails = () => {
    const { isActive, isComplete, isIdle, resetStep, currentStepConfig } =
        useMultiStepFormContext<ManifestFormFields>();
    const { getFieldHelpers, getFieldMeta, getFieldProps, values, setFieldValue } =
        useFormikContext<ManifestFormFields>();
    const { countries } = useCountries();

    //TODO: For now, we hardcode the code lists for AIS.
    // We should be able to get the `market` and `declarationType` from the manifest / tenant or team
    const { aisCodelists, getIrelandCodelists } = useCodelists();

    useEffect(() => {
        getIrelandCodelists(DeclarationTypes.AIS);
    }, [getIrelandCodelists]);

    const [disabledDeparturePort, setDisabledDeparturePort] = useState(false);

    const disabled = currentStepConfig?.disabled ?? false;
    const stepKey = ManifestFormSteps.TRANSPORT_DETAILS;

    const formattedArrivalDateTime = values.transportDetails.arrivalDateTime
        ? moment(values.transportDetails.arrivalDateTime).format(DATE_TIME_FORMAT)
        : '';
    const formattedDepartureDateTime = values.transportDetails.departureDateTime
        ? moment(values.transportDetails.departureDateTime).format(DATE_TIME_FORMAT)
        : '';
    const selectedCountryOfDispatch =
        countries.list.find((c) => c.countryCode === values.transportDetails.countryOfDispatch)?.name ?? '';
    const selectedNationalityTransportMeansCountry =
        countries.list.find((c) => c.countryCode === values.transportDetails.nationalityTransportMeans)?.name ?? '';
    const selectedTransportMeansIdType =
        aisCodelists?.meansIdentityType.find((el) => el.id === values.transportDetails.meansIdentificationType)
            ?.value ?? '';
    const CODELIST_REG_NUMBER_OF_ROAD_VEHICLE = aisCodelists?.meansIdentityType.find((el) => el.id === '30')?.id;
    const CODELIST_IMO_SHIP_IDENTIFICATION_NUMBER = aisCodelists?.meansIdentityType.find((el) => el.id === '10')?.id;

    const transportTypeValue = getTransportTypeDisplay(values.transportDetails.transportTypeId);
    const containerStatusReadValue = getContainerStatusLabel(values.transportDetails.containerIndication) ?? '-';

    const isAccompanied = useMemo(
        () => values.transportDetails.containerIndication === ContainerStatuses.ACCOMPANIED,
        [values.transportDetails.containerIndication]
    );

    const meansIdentityTypeOptions = useMemo(
        () =>
            aisCodelists?.meansIdentityType
                ? [...aisCodelists.meansIdentityType].sort((a, b) => Number(a.id) - Number(b.id))
                : [],
        [aisCodelists?.meansIdentityType]
    );

    const handleReset = (fieldKey: TransportDetailsResetKey) => {
        resetStep(stepKey, fieldKey);
    };

    // Update the defaults based on accompanied selection (container indication)
    useEffect(() => {
        if (isAccompanied) {
            if (values.transportDetails.transportTypeId !== TransportTypes.ROAD) {
                setFieldValue(TransportDetailsFields.path.transportTypeId, TransportTypes.ROAD, true);
            }

            if (
                CODELIST_REG_NUMBER_OF_ROAD_VEHICLE &&
                values.transportDetails.meansIdentificationType !== CODELIST_REG_NUMBER_OF_ROAD_VEHICLE
            ) {
                setFieldValue(
                    TransportDetailsFields.path.meansIdentificationType,
                    CODELIST_REG_NUMBER_OF_ROAD_VEHICLE,
                    true
                );
            }
        } else {
            if (values.transportDetails.transportTypeId !== TransportTypes.MARITIME) {
                setFieldValue(TransportDetailsFields.path.transportTypeId, TransportTypes.MARITIME, true);
            }

            if (
                CODELIST_IMO_SHIP_IDENTIFICATION_NUMBER &&
                values.transportDetails.meansIdentificationType !== CODELIST_IMO_SHIP_IDENTIFICATION_NUMBER
            ) {
                setFieldValue(
                    TransportDetailsFields.path.meansIdentificationType,
                    CODELIST_IMO_SHIP_IDENTIFICATION_NUMBER,
                    true
                );
            }
        }
        // Update only when the container indication is changed so we allow to override the defaults
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAccompanied]);

    // If the country of dispatch is different than UK,
    // reset and disable the departure port
    useEffect(() => {
        if (values.transportDetails.countryOfDispatch !== UK_COUNTRY_CODE) {
            setFieldValue(TransportDetailsFields.path.departurePort, undefined);
            setDisabledDeparturePort(true);
        } else {
            setDisabledDeparturePort(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.transportDetails.countryOfDispatch]);

    if (isIdle(ManifestFormSteps.TRANSPORT_DETAILS)) {
        return <FormSectionHeader title="Transport Details" icon={<TruckIconOutlined />} />;
    }

    if (isComplete(ManifestFormSteps.TRANSPORT_DETAILS)) {
        return (
            <Space style={{ width: '100%' }} direction="vertical" size={8}>
                <FormSectionHeader title="Transport Details" icon={<TruckIconOutlined />} />
                <ReadonlyFormItem
                    label="Container Indication"
                    onReset={() => handleReset(TransportDetailsFields.resetKey.containerIndication)}
                    value={containerStatusReadValue}
                    disabled={disabled}
                />
                <ReadonlyFormItem
                    label="Transport Type"
                    onReset={() => handleReset(TransportDetailsFields.resetKey.transportTypeId)}
                    value={transportTypeValue ?? '-'}
                    disabled={disabled}
                />
                <ReadonlyFormItem
                    label="Carrier/Haulier"
                    onReset={() => handleReset(TransportDetailsFields.resetKey.carrierOrHaulier)}
                    value={values.transportDetails?.carrierOrHaulier ?? '-'}
                    disabled={disabled}
                />
                <ReadonlyFormItem
                    label="Country of Dispatch"
                    onReset={() => handleReset(TransportDetailsFields.resetKey.countryOfDispatch)}
                    value={selectedCountryOfDispatch}
                    disabled={disabled}
                />
                <ReadonlyFormItem
                    label="Departure Port"
                    onReset={() => handleReset(TransportDetailsFields.resetKey.departurePort)}
                    value={values.transportDetails?.departurePort ?? '-'}
                    disabled={disabled}
                />
                <ReadonlyFormItem
                    label="Customs Office of Presentation"
                    onReset={() => handleReset(TransportDetailsFields.resetKey.customsOfficeOfPresentation)}
                    value={values.transportDetails?.customsOfficeOfPresentation ?? '-'}
                    disabled={disabled}
                />
                <ReadonlyFormItem
                    label="Transport Means ID Type"
                    onReset={() => handleReset(TransportDetailsFields.resetKey.meansIdentificationType)}
                    value={selectedTransportMeansIdType}
                    disabled={disabled}
                />
                <ReadonlyFormItem
                    label="Transport Means ID"
                    onReset={() => handleReset(TransportDetailsFields.resetKey.meansId)}
                    value={values.transportDetails?.meansId ?? '-'}
                    disabled={disabled}
                />
                <ReadonlyFormItem
                    label="Nationality Transport Means"
                    onReset={() => handleReset(TransportDetailsFields.resetKey.nationalityTransportMeans)}
                    value={selectedNationalityTransportMeansCountry}
                    disabled={disabled}
                />

                {isAccompanied ? (
                    <ReadonlyFormItem
                        label="RoRo IMO Number"
                        onReset={() => handleReset(TransportDetailsFields.resetKey.vehicleRegistration)}
                        value={values.transportDetails.vehicleRegistration ?? '-'}
                        disabled={disabled}
                    />
                ) : (
                    <ReadonlyFormItem
                        label="Trailer ID"
                        onReset={() => handleReset(TransportDetailsFields.resetKey.vehicleRegistration)}
                        value={values.transportDetails.vehicleRegistration ?? '-'}
                        disabled={disabled}
                    />
                )}

                <ReadonlyFormItem
                    label="Date and Time of Departure"
                    onReset={() => handleReset(TransportDetailsFields.resetKey.departureDateTime)}
                    value={formattedDepartureDateTime}
                    disabled={disabled}
                />
                <ReadonlyFormItem
                    label="Date and Time of Arrival"
                    onReset={() => handleReset(TransportDetailsFields.resetKey.arrivalDateTime)}
                    value={formattedArrivalDateTime}
                    disabled={disabled}
                />
            </Space>
        );
    }

    if (isActive(ManifestFormSteps.TRANSPORT_DETAILS)) {
        return (
            <Space style={{ width: '100%' }} direction="vertical" size={16}>
                <FormSectionHeader
                    title="Transport Details"
                    icon={<TruckIconOutlined />}
                    subtitle="Please provide the following transport details for this manifest"
                />
                <ContainerStatusField
                    required
                    label="Container Indication:"
                    refNumber="7.2"
                    fieldProps={getFieldProps(TransportDetailsFields.path.containerIndication)}
                    fieldMeta={getFieldMeta(TransportDetailsFields.path.containerIndication)}
                    fieldHelpers={getFieldHelpers(TransportDetailsFields.path.containerIndication)}
                />
                <FormSelect
                    style={{
                        maxWidth: FORM_FIELDS_MAX_WIDTH,
                    }}
                    required
                    value={transportTypeValue}
                    refNumber="7.4"
                    label="Transport Type:"
                    placeholder="Select transport type"
                    fieldProps={getFieldProps(TransportDetailsFields.path.transportTypeId)}
                    fieldMeta={getFieldMeta(TransportDetailsFields.path.transportTypeId)}
                    fieldHelper={getFieldHelpers(TransportDetailsFields.path.transportTypeId)}
                    selectOptions={TransportTypeOptions}
                />
                <FormInput
                    inputStyles={{
                        maxWidth: FORM_FIELDS_MAX_WIDTH,
                    }}
                    label="Carrier/Haulier:"
                    placeholder="Enter Carrier/Haulier"
                    fieldProps={getFieldProps(TransportDetailsFields.path.carrierOrHaulier)}
                    fieldMeta={getFieldMeta(TransportDetailsFields.path.carrierOrHaulier)}
                    fieldHelper={getFieldHelpers(TransportDetailsFields.path.carrierOrHaulier)}
                />
                <CountrySelect
                    style={{
                        maxWidth: FORM_FIELDS_MAX_WIDTH,
                    }}
                    required
                    refNumber="5.14"
                    label="Country of Dispatch:"
                    placeholder="Select country of dispatch"
                    fieldProps={getFieldProps(TransportDetailsFields.path.countryOfDispatch)}
                    fieldMeta={getFieldMeta(TransportDetailsFields.path.countryOfDispatch)}
                    fieldHelper={getFieldHelpers(TransportDetailsFields.path.countryOfDispatch)}
                />

                <UnLocodeSelect
                    required
                    disabled={disabledDeparturePort}
                    countryCode={UK_COUNTRY_CODE}
                    label="Departure Port:"
                    placeholder="Select Departure Port"
                    fieldProps={getFieldProps(TransportDetailsFields.path.departurePort)}
                    fieldMeta={getFieldMeta(TransportDetailsFields.path.departurePort)}
                    fieldHelper={getFieldHelpers(TransportDetailsFields.path.departurePort)}
                    style={{
                        maxWidth: FORM_FIELDS_MAX_WIDTH,
                    }}
                />

                <FormSelect
                    style={{
                        maxWidth: FORM_FIELDS_MAX_WIDTH,
                    }}
                    required
                    refNumber="5.26"
                    label="Customs Office of Presentation:"
                    placeholder="Select customs office of presentation"
                    fieldProps={getFieldProps(TransportDetailsFields.path.customsOfficeOfPresentation)}
                    fieldMeta={getFieldMeta(TransportDetailsFields.path.customsOfficeOfPresentation)}
                    fieldHelper={getFieldHelpers(TransportDetailsFields.path.customsOfficeOfPresentation)}
                    selectOptions={aisCodelists?.nationalCustomsOffice}
                />
                <FormSelect
                    style={{
                        maxWidth: FORM_FIELDS_MAX_WIDTH,
                    }}
                    required
                    refNumber="7.9"
                    label="Transport Means ID Type:"
                    placeholder="Select transport means ID type"
                    selectOptions={meansIdentityTypeOptions}
                    fieldProps={getFieldProps(TransportDetailsFields.path.meansIdentificationType)}
                    fieldMeta={getFieldMeta(TransportDetailsFields.path.meansIdentificationType)}
                    fieldHelper={getFieldHelpers(TransportDetailsFields.path.meansIdentificationType)}
                />
                <FormInput
                    inputStyles={{
                        maxWidth: FORM_FIELDS_MAX_WIDTH,
                    }}
                    required
                    refNumber="7.9"
                    label="Transport Means ID:"
                    placeholder="Enter means of transport ID"
                    fieldProps={getFieldProps(TransportDetailsFields.path.meansId)}
                    fieldMeta={getFieldMeta(TransportDetailsFields.path.meansId)}
                    fieldHelper={getFieldHelpers(TransportDetailsFields.path.meansId)}
                />
                <CountrySelect
                    style={{
                        maxWidth: FORM_FIELDS_MAX_WIDTH,
                    }}
                    required
                    refNumber="7.15"
                    label="Nationality Transport Means"
                    placeholder="Select nationality transport means"
                    fieldProps={getFieldProps(TransportDetailsFields.path.nationalityTransportMeans)}
                    fieldMeta={getFieldMeta(TransportDetailsFields.path.nationalityTransportMeans)}
                    fieldHelper={getFieldHelpers(TransportDetailsFields.path.nationalityTransportMeans)}
                />

                {isAccompanied ? (
                    <FormInput
                        inputStyles={{
                            maxWidth: FORM_FIELDS_MAX_WIDTH,
                        }}
                        refNumber="1D94"
                        label="RoRo IMO Number:"
                        placeholder="Enter IMO"
                        fieldProps={getFieldProps(TransportDetailsFields.path.vehicleRegistration)}
                        fieldMeta={getFieldMeta(TransportDetailsFields.path.vehicleRegistration)}
                        fieldHelper={getFieldHelpers(TransportDetailsFields.path.vehicleRegistration)}
                    />
                ) : (
                    <FormInput
                        inputStyles={{
                            maxWidth: FORM_FIELDS_MAX_WIDTH,
                        }}
                        refNumber="1D95"
                        label="Trailer ID:"
                        placeholder="Enter Trailer ID"
                        fieldProps={getFieldProps(TransportDetailsFields.path.vehicleRegistration)}
                        fieldMeta={getFieldMeta(TransportDetailsFields.path.vehicleRegistration)}
                        fieldHelper={getFieldHelpers(TransportDetailsFields.path.vehicleRegistration)}
                    />
                )}

                <FormDatePicker
                    style={{
                        pickerWrapper: {
                            width: FORM_FIELDS_MAX_WIDTH,
                        },
                    }}
                    showTime
                    placeholder="Select date and time of departure"
                    label="Date and Time of Departure:"
                    fieldName={TransportDetailsFields.path.departureDateTime}
                    refNumber="1D23"
                    serializeDate={(value) => (value ? moment(value).format('YYYY-MM-DDTHH:mm:ss') : '')}
                />
                <FormDatePicker
                    style={{
                        pickerWrapper: {
                            width: FORM_FIELDS_MAX_WIDTH,
                        },
                    }}
                    required
                    showTime
                    placeholder="Select date and time of arrival"
                    label="Date and Time of Arrival:"
                    fieldName={TransportDetailsFields.path.arrivalDateTime}
                    refNumber="1D24"
                    serializeDate={(value) => (value ? moment(value).format('YYYY-MM-DDTHH:mm:ss') : '')}
                />
            </Space>
        );
    }

    return null;
};

export default TransportDetails;
